<template>
  <transition>
    <div v-if="showFailedQuiz" key="failed">
      <h3>Try again</h3>
      <p class="mb-5">
        You did not pass the quiz and must retake the quiz to continue. Please click the button below to try again.
      </p>
      
      <d-btn color="secondary-500" :loading="loading" @click="tryAgain">
        Retry
      </d-btn>
      
      <quiz-feedback :quiz-result="quizResult" :quiz-questions="quizQuestions" />
    </div>
    <div v-else key="passed">
        <h3>{{ contentItem.name }}</h3>
        <div class="pb-5 rich-text" v-html="contentItem.content" />

        <div v-if="needPasscode && (!quizResult || !quizResult.pass)" class="col-4">
            <d-field label="Enter your quiz passcode" rules="required">
                <d-input v-model="passcode" name="passcode" type="password" required style="width:200px" />
            </d-field>
            <d-btn color="success" @click="validateQuizPasscode">
                Start Quiz
            </d-btn>
        </div>
        <div v-else>
            <!-- Loading State -->
            <div v-if="loading" class="text-center flex justify-center py-8">
                <div class="-ml-6">
                    <d-loader />
                    <p>Loading Questions...</p>
                </div>
            </div>

            <!-- Quiz In Progress -->
            <div v-else-if="!quizResult || !quizResult.completed">
                <quiz-question 
                    v-for="(question, index) in quizQuestions" 
                    :key="question.id" 
                    :question-number="index + 1" 
                    :question="question" 
                    :quiz-result="quizResult" 
                    :class="{ 'disabled-div': lockOnPass && passed }"
                />
            </div>

            <!-- Quiz Completed - Show Feedback -->
            <div v-else>
                <div v-if="quizResult.pass" class="bg-green-50 border-l-4 border-green-500 p-4 mb-5">
                    <p class="text-green-800 font-semibold">
                        <d-icon icon="check-circle" class="mr-2" />
                        You've passed this quiz! You can click 'Next' to continue.
                    </p>
                </div>
                
                <quiz-feedback :quiz-result="quizResult" :quiz-questions="quizQuestions" />
            </div>
        </div>

        <slot name="pagination" :passed="quizResult && quizResult.pass" :check-if-passed="checkIfPassed" :submitting="submitting" />
    </div>

  </transition>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import QuizQuestion from './QuizQuestion.vue';
  import QuizFeedback from './QuizFeedback.vue';

  export default {
    components: {
        QuizQuestion,
        QuizFeedback
    },

    props: {
      contentItem: {
        type: Object,
        default: () => ({}),
      },
    },

    setup(props) {
      const loading = ref(true);
      const submitting = ref(false);
      const showFailedQuiz = ref(false);
      const needPasscode = ref(false);
      const lockOnPass = ref(false);
      const quizResult = ref(null);
      const passcode = ref('');
      const quizQuestions = ref([]);
      const toast = useToast();

      const passed = computed(() => {
        return quizResult.value && quizResult.value.pass;
      });

      const getQuizQuestions = async () => {
        try {
          const { data } = await axios.get(`/api/content-listings/items/quiz/${props.contentItem.id}/questions`);
          quizQuestions.value = data;
        } catch (error) {
          console.error('Error loading quiz questions:', error);
          toast.error('Failed to load quiz questions. Please try again.');
        }
      };

      const validateQuizPasscode = async () => {
        if (passcode.value === props.contentItem.password) {
          needPasscode.value = false;
          toast.success('Quiz passcode accepted! You can now start the quiz.');
        } else {
          toast.error('Invalid quiz passcode. Please try again.');
        }
      };

      const getResults = async () => {
        try {
          const { data } = await axios.get(`/api/content-listings/items/quiz/${props.contentItem.id}/results`);
          if (!data) {
            const { data: newResult } = await axios.post(`/api/content-listings/items/quiz/${props.contentItem.id}`);
            quizResult.value = newResult.resultObject;
          } else {
            quizResult.value = data;
          }
        } catch (error) {
          console.error('Error loading quiz results:', error);
          toast.error('Failed to load quiz results. Please try again.');
          quizResult.value = null;
        } finally {
          loading.value = false;
        }
      };

      const tryAgain = async () => {
        loading.value = true;
        await resetQuiz();
        await getResults();
        showFailedQuiz.value = false;
      };

      const resetQuiz = async () => {
        try {
          await axios.delete(`/api/content-listings/items/quiz/${props.contentItem.id}`);
          toast.info('Quiz has been reset. You can now retake it.');
        } catch (error) {
          console.error('Error resetting quiz:', error);
          toast.error('Failed to reset quiz. Please try again.');
        }
      };

      const checkIfPassed = async () => {
        submitting.value = true;
        await getResults();
        if (!quizResult.value || !quizResult.value.pass) {
          showFailedQuiz.value = true;
          toast.error('You did not pass the quiz. Please review the questions and try again.');
        } else {
          toast.success('Congratulations! You have completed and passed this quiz! You may continue to the next step when you are ready.');
        }
        submitting.value = false;
      };

      onMounted(async () => {
        needPasscode.value = props.contentItem.password != null && props.contentItem.password !== "";
        lockOnPass.value = props.contentItem.lockOnPass != null && props.contentItem.lockOnPass;
        await getQuizQuestions();
        await getResults();
      });

      return {
        loading,
        submitting,
        showFailedQuiz,
        needPasscode,
        lockOnPass,
        quizResult,
        passcode,
        quizQuestions,
        passed,
        getQuizQuestions,
        validateQuizPasscode,
        getResults,
        tryAgain,
        resetQuiz,
        checkIfPassed,
      };
    },
  };
</script>

<style scoped>
.disabled-div {
  pointer-events: none;
  opacity: 0.5;
}
</style>